import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { BASE_URL } from '../services/api';

const FeedbackManagement = ({ selectedFeedbackId }) => {
  const [feedbackList, setFeedbackList] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    priority: ''
  });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const detailsRef = useRef(null);

  const feedbackTypes = {
    bug: { label: 'Bug Report', icon: '🐛', color: '#e53e3e' },
    feature_request: { label: 'Feature Request', icon: '💡', color: '#805ad5' },
    question_suggestion: { label: 'Question Suggestion', icon: '❓', color: '#3182ce' },
    general_message: { label: 'General Message', icon: '💬', color: '#38a169' }
  };

  const statusColors = {
    pending: '#ed8936',
    in_progress: '#4299e1',
    resolved: '#48bb78',
    closed: '#718096'
  };

  const priorityColors = {
    low: '#68d391',
    medium: '#4299e1',
    high: '#ed8936',
    critical: '#fc8181'
  };

  useEffect(() => {
    fetchFeedback();
    fetchStatistics();
  }, [filters]);

  useEffect(() => {
    if (selectedFeedbackId && feedbackList.length > 0) {
      const feedback = feedbackList.find(f => f._id === selectedFeedbackId);
      if (feedback) {
        setSelectedFeedback(feedback);
        setTimeout(() => {
          if (detailsRef.current) {
            detailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  }, [selectedFeedbackId, feedbackList]);

  const fetchFeedback = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);
      if (filters.priority) params.append('priority', filters.priority);

      const response = await axios.get(
        `${BASE_URL}/api/feedback/admin/all?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setFeedbackList(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching feedback:', err);
      setError('Failed to load feedback');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStatistics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BASE_URL}/api/feedback/admin/statistics`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setStatistics(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleStatusUpdate = async (feedbackId, newStatus, priority, adminNotes) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${BASE_URL}/api/feedback/${feedbackId}/status`,
        { status: newStatus, priority, adminNotes },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update local state
      setFeedbackList(prev =>
        prev.map(f => f._id === feedbackId ? response.data.feedback : f)
      );

      if (selectedFeedback && selectedFeedback._id === feedbackId) {
        setSelectedFeedback(response.data.feedback);
      }

      fetchStatistics();
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedFeedback) return;

    setIsSendingMessage(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/api/feedback/${selectedFeedback._id}/communicate`,
        { message: newMessage },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSelectedFeedback(response.data.feedback);
      setFeedbackList(prev =>
        prev.map(f => f._id === selectedFeedback._id ? response.data.feedback : f)
      );
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleDelete = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/feedback/${feedbackId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setFeedbackList(prev => prev.filter(f => f._id !== feedbackId));
      if (selectedFeedback && selectedFeedback._id === feedbackId) {
        setSelectedFeedback(null);
      }
      fetchStatistics();
    } catch (err) {
      console.error('Error deleting feedback:', err);
      alert('Failed to delete feedback');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1600px', margin: '0 auto', backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{
        marginBottom: '24px',
        backgroundColor: '#fff',
        padding: '20px 24px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e2e8f0'
      }}>
        <h1 style={{
          fontSize: '22px',
          fontWeight: '600',
          color: '#2d3748',
          marginBottom: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span>💬</span>
          <span>User Feedback Tracker</span>
        </h1>
        <p style={{ fontSize: '13px', color: '#718096', margin: 0 }}>
          Manage user feedback, bug reports, and feature requests
        </p>
      </div>

      {/* Statistics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '16px',
        marginBottom: '24px'
      }}>
        <div style={{
          backgroundColor: '#fff',
          padding: '18px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ fontSize: '13px', color: '#718096', marginBottom: '6px', fontWeight: '500' }}>
            Total Feedback
          </div>
          <div style={{ fontSize: '28px', fontWeight: '600', color: '#2d3748' }}>
            {statistics.total || 0}
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff',
          padding: '18px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ fontSize: '13px', color: '#718096', marginBottom: '6px', fontWeight: '500' }}>
            Pending
          </div>
          <div style={{ fontSize: '28px', fontWeight: '600', color: '#ed8936' }}>
            {statistics.byStatus?.pending || 0}
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff',
          padding: '18px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ fontSize: '13px', color: '#718096', marginBottom: '6px', fontWeight: '500' }}>
            In Progress
          </div>
          <div style={{ fontSize: '28px', fontWeight: '600', color: '#4299e1' }}>
            {statistics.byStatus?.in_progress || 0}
          </div>
        </div>

        <div style={{
          backgroundColor: '#fff',
          padding: '18px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ fontSize: '13px', color: '#718096', marginBottom: '6px', fontWeight: '500' }}>
            Resolved
          </div>
          <div style={{ fontSize: '28px', fontWeight: '600', color: '#48bb78' }}>
            {statistics.byStatus?.resolved || 0}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: '#fff',
        padding: '18px 20px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
        marginBottom: '20px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px'
        }}>
          <div>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '500',
              color: '#4a5568',
              marginBottom: '6px'
            }}>
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#2d3748',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '500',
              color: '#4a5568',
              marginBottom: '6px'
            }}>
              Type
            </label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#2d3748',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="">All Types</option>
              <option value="bug">Bug Report</option>
              <option value="feature_request">Feature Request</option>
              <option value="question_suggestion">Question Suggestion</option>
              <option value="general_message">General Message</option>
            </select>
          </div>

          <div>
            <label style={{
              display: 'block',
              fontSize: '12px',
              fontWeight: '500',
              color: '#4a5568',
              marginBottom: '6px'
            }}>
              Priority
            </label>
            <select
              value={filters.priority}
              onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#2d3748',
                backgroundColor: '#fff',
                cursor: 'pointer'
              }}
            >
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#fff5f5',
          border: '1px solid #fc8181',
          borderRadius: '8px',
          marginBottom: '20px',
          color: '#c53030',
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '60px', color: '#718096', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
          Loading feedback...
        </div>
      ) : feedbackList.length === 0 ? (
        <div style={{
          backgroundColor: '#fff',
          padding: '60px',
          borderRadius: '8px',
          textAlign: 'center',
          color: '#718096',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
          <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '6px', color: '#2d3748' }}>
            No feedback found
          </div>
          <div style={{ fontSize: '13px' }}>
            Adjust your filters or wait for users to submit feedback
          </div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: selectedFeedback ? '420px 1fr' : '1fr',
          gap: '20px',
          alignItems: 'start'
        }}>
          {/* Feedback List */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            maxHeight: 'calc(100vh - 350px)',
            overflowY: 'auto',
            paddingRight: '8px'
          }}>
            {feedbackList.map(feedback => {
              const typeInfo = feedbackTypes[feedback.type];
              return (
                <div
                  key={feedback._id}
                  onClick={() => setSelectedFeedback(feedback)}
                  style={{
                    backgroundColor: '#fff',
                    padding: '14px',
                    borderRadius: '8px',
                    border: selectedFeedback?._id === feedback._id
                      ? '2px solid #4299e1'
                      : '1px solid #e2e8f0',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    boxShadow: selectedFeedback?._id === feedback._id
                      ? '0 2px 8px rgba(66, 153, 225, 0.15)'
                      : '0 1px 2px rgba(0, 0, 0, 0.04)'
                  }}
                  onMouseOver={(e) => {
                    if (selectedFeedback?._id !== feedback._id) {
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08)';
                      e.currentTarget.style.borderColor = '#cbd5e0';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (selectedFeedback?._id !== feedback._id) {
                      e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.04)';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '8px',
                    gap: '8px'
                  }}>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '3px 8px',
                      backgroundColor: `${typeInfo.color}12`,
                      borderRadius: '5px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: typeInfo.color
                    }}>
                      <span>{typeInfo.icon}</span>
                      <span>{typeInfo.label}</span>
                    </div>

                    <div style={{
                      display: 'inline-block',
                      padding: '3px 8px',
                      backgroundColor: `${statusColors[feedback.status]}12`,
                      color: statusColors[feedback.status],
                      borderRadius: '5px',
                      fontSize: '10px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {feedback.status.replace('_', ' ')}
                    </div>
                  </div>

                  <h3 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#2d3748',
                    marginBottom: '6px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {feedback.title}
                  </h3>

                  <div style={{
                    fontSize: '12px',
                    color: '#718096',
                    marginBottom: '10px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: '1.4'
                  }}>
                    {feedback.description}
                  </div>

                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    fontSize: '11px',
                    color: '#a0aec0'
                  }}>
                    <span>by {feedback.user?.name}</span>
                    <span>{formatDate(feedback.createdAt)}</span>
                  </div>

                  {feedback.communications && feedback.communications.length > 0 && (
                    <div style={{
                      marginTop: '8px',
                      padding: '5px 8px',
                      backgroundColor: '#f7fafc',
                      borderRadius: '5px',
                      fontSize: '10px',
                      color: '#4a5568',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <span>💬</span>
                      <span>{feedback.communications.length} message(s)</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Feedback Details */}
          {selectedFeedback && (
            <div
              ref={detailsRef}
              style={{
                backgroundColor: '#fff',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e2e8f0',
                maxHeight: 'calc(100vh - 350px)',
                overflow: 'auto'
              }}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '18px',
                paddingBottom: '18px',
                borderBottom: '1px solid #e2e8f0'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px'
                  }}>
                    <span style={{ fontSize: '24px' }}>
                      {feedbackTypes[selectedFeedback.type].icon}
                    </span>
                    <h2 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#2d3748',
                      margin: 0
                    }}>
                      {selectedFeedback.title}
                    </h2>
                  </div>

                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <span style={{
                      padding: '3px 10px',
                      backgroundColor: `${feedbackTypes[selectedFeedback.type].color}12`,
                      color: feedbackTypes[selectedFeedback.type].color,
                      borderRadius: '5px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {feedbackTypes[selectedFeedback.type].label}
                    </span>

                    <span style={{
                      padding: '3px 10px',
                      backgroundColor: `${priorityColors[selectedFeedback.priority]}12`,
                      color: priorityColors[selectedFeedback.priority],
                      borderRadius: '5px',
                      fontSize: '12px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {selectedFeedback.priority} Priority
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => setSelectedFeedback(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    color: '#718096',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    lineHeight: 1
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  ×
                </button>
              </div>

              {/* User Info */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '6px'
                }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '50%',
                    backgroundColor: '#4299e1',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: '600',
                    flexShrink: 0
                  }}>
                    {selectedFeedback.user?.name?.charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#2d3748'
                    }}>
                      {selectedFeedback.user?.name}
                    </div>
                    <div style={{ fontSize: '12px', color: '#718096' }}>
                      {selectedFeedback.user?.email}
                    </div>
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#a0aec0',
                    flexShrink: 0
                  }}>
                    {formatDate(selectedFeedback.createdAt)}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#2d3748',
                  marginBottom: '6px'
                }}>
                  Description
                </h4>
                <div style={{
                  padding: '10px 12px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '6px',
                  fontSize: '13px',
                  color: '#4a5568',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.5'
                }}>
                  {selectedFeedback.description}
                </div>
              </div>

              {/* Attachments */}
              {selectedFeedback.attachments && selectedFeedback.attachments.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h4 style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#2d3748',
                    marginBottom: '6px'
                  }}>
                    Attachments
                  </h4>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
                    gap: '10px'
                  }}>
                    {selectedFeedback.attachments.map((attachment, idx) => (
                      <div key={idx} style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        backgroundColor: '#fff'
                      }}>
                        {attachment.type === 'image' ? (
                          <a href={attachment.url} target="_blank" rel="noopener noreferrer">
                            <img
                              src={attachment.url}
                              alt={attachment.filename}
                              style={{
                                width: '100%',
                                height: '100px',
                                objectFit: 'cover',
                                cursor: 'pointer'
                              }}
                            />
                          </a>
                        ) : attachment.type === 'video' ? (
                          <video
                            controls
                            style={{
                              width: '100%',
                              height: '100px',
                              backgroundColor: '#000'
                            }}
                          >
                            <source src={attachment.url} />
                          </video>
                        ) : (
                          <a
                            href={attachment.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100px',
                              fontSize: '40px',
                              backgroundColor: '#f7fafc',
                              textDecoration: 'none'
                            }}
                          >
                            📄
                          </a>
                        )}
                        <div style={{
                          padding: '5px 8px',
                          fontSize: '10px',
                          color: '#718096',
                          borderTop: '1px solid #e2e8f0',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {attachment.filename}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Controls */}
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#2d3748',
                  marginBottom: '8px'
                }}>
                  Admin Actions
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '10px'
                }}>
                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '11px',
                      fontWeight: '500',
                      color: '#4a5568',
                      marginBottom: '5px'
                    }}>
                      Status
                    </label>
                    <select
                      value={selectedFeedback.status}
                      onChange={(e) => handleStatusUpdate(
                        selectedFeedback._id,
                        e.target.value,
                        selectedFeedback.priority,
                        selectedFeedback.adminNotes
                      )}
                      style={{
                        width: '100%',
                        padding: '7px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#2d3748',
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  <div>
                    <label style={{
                      display: 'block',
                      fontSize: '11px',
                      fontWeight: '500',
                      color: '#4a5568',
                      marginBottom: '5px'
                    }}>
                      Priority
                    </label>
                    <select
                      value={selectedFeedback.priority}
                      onChange={(e) => handleStatusUpdate(
                        selectedFeedback._id,
                        selectedFeedback.status,
                        e.target.value,
                        selectedFeedback.adminNotes
                      )}
                      style={{
                        width: '100%',
                        padding: '7px 10px',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#2d3748',
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={() => handleDelete(selectedFeedback._id)}
                  style={{
                    marginTop: '10px',
                    padding: '7px 14px',
                    border: 'none',
                    borderRadius: '6px',
                    backgroundColor: '#fff5f5',
                    color: '#e53e3e',
                    fontSize: '12px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    width: '100%'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#fed7d7'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#fff5f5'}
                >
                  Delete Feedback
                </button>
              </div>

              {/* Communications */}
              <div style={{ marginBottom: '0' }}>
                <h4 style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#2d3748',
                  marginBottom: '8px'
                }}>
                  Communication
                </h4>

                {/* Messages */}
                <div style={{
                  maxHeight: '280px',
                  overflowY: 'auto',
                  marginBottom: '10px',
                  padding: '10px',
                  backgroundColor: '#f7fafc',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0'
                }}>
                  {selectedFeedback.communications && selectedFeedback.communications.length > 0 ? (
                    selectedFeedback.communications.map((comm, idx) => (
                      <div
                        key={idx}
                        style={{
                          marginBottom: idx < selectedFeedback.communications.length - 1 ? '10px' : '0',
                          padding: '10px',
                          backgroundColor: comm.senderRole === 'admin' ? '#ebf8ff' : '#fff',
                          borderRadius: '6px',
                          border: '1px solid #e2e8f0'
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginBottom: '5px',
                          alignItems: 'center'
                        }}>
                          <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#2d3748',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}>
                            {comm.sender?.name}
                            <span style={{
                              padding: '2px 6px',
                              backgroundColor: comm.senderRole === 'admin' ? '#4299e1' : '#718096',
                              color: '#fff',
                              borderRadius: '3px',
                              fontSize: '9px',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.3px'
                            }}>
                              {comm.senderRole}
                            </span>
                          </span>
                          <span style={{ fontSize: '10px', color: '#a0aec0' }}>
                            {formatDate(comm.createdAt)}
                          </span>
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#4a5568',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.5'
                        }}>
                          {comm.message}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#a0aec0',
                      fontSize: '12px'
                    }}>
                      No messages yet
                    </div>
                  )}
                </div>

                {/* New Message Input */}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={2}
                    style={{
                      flex: 1,
                      padding: '8px 10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#2d3748',
                      resize: 'vertical',
                      fontFamily: 'inherit'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() || isSendingMessage}
                    style={{
                      padding: '8px 16px',
                      border: 'none',
                      borderRadius: '6px',
                      backgroundColor: !newMessage.trim() || isSendingMessage
                        ? '#cbd5e0'
                        : '#4299e1',
                      color: '#fff',
                      fontSize: '12px',
                      fontWeight: '500',
                      cursor: !newMessage.trim() || isSendingMessage
                        ? 'not-allowed'
                        : 'pointer',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {isSendingMessage ? 'Sending...' : 'Send'}
                  </button>
                </div>
                <div style={{
                  marginTop: '5px',
                  fontSize: '10px',
                  color: '#a0aec0'
                }}>
                  Press Ctrl+Enter to send
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FeedbackManagement;
